import { NgFor, NgIf } from '@angular/common';
import {
	ChangeDetectorRef,
	Component,
	ElementRef,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Optional,
	SimpleChanges,
	ViewChild,
	inject
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BehaviorSubject, Subject, of } from 'rxjs';
import { distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import {
	ReportInlineEditService,
	ReportInlineTextField,
	normalizeInlineText
} from '../../../../services/report-inline-edit.service';
import { ReportFigureFlagsService } from '../../../../services/report-figure-flags.service';
import {
	ReportFigureFlag,
	figurePlace,
	markedRuns
} from '../../../../services/report-quoted-figures';
import { ReportFigureFlagsComponent } from '../report-figure-flags/report-figure-flags.component';
import { ReportRichTextComponent } from '../report-rich-text/report-rich-text.component';

const NO_MARKS: ReadonlySet<string> = new Set();

/**
 * `plaintext-only` keeps pasted and dropped formatting out of the field. An
 * engine that does not know the value throws on it or ignores it; that one gets
 * `true`, and the paste handler below still inserts plain text.
 */
const EDITABLE_MODE: 'plaintext-only' | 'true' = (() => {
	const probe = document.createElement('span');
	try {
		probe.contentEditable = 'plaintext-only';
	} catch {
		return 'true';
	}
	return probe.contentEditable === 'plaintext-only'
		? 'plaintext-only'
		: 'true';
})();

/**
 * One text on a slide — a heading, a summary, the cover's title — that the
 * reader can click and type into, in the editor and nowhere else.
 *
 * Outside the editor it is the text and nothing more, so the PDF export and a
 * read-only share draw exactly what they drew before. In the editor the text is
 * edited where it stands: hover shows an outline, focus a stronger one, and
 * neither changes the layout. Enter (or leaving the field) keeps what was
 * typed, Esc puts back what was there, and a field that takes more than one
 * line starts a new one on Shift+Enter. Inside the field the browser's own undo
 * applies; the page's history takes over once the edit is committed.
 *
 * The text is written into the element as `textContent` and read back with
 * `innerText`, never interpolated: typing replaces the text nodes an
 * interpolation would keep writing to, and the page would then update a node
 * that is no longer on it.
 *
 * A field that carries formatting — a section's summary (`field.rich`) — is
 * handed to the rich-text field instead, on every surface, so the sixteen
 * slides draw a formatted summary without knowing that it is one.
 *
 * In the editor, a number in the text that quotes a figure the live data has
 * since moved on from is marked where it stands, and flagged under the text
 * (`app-report-figure-flags`).
 */
@Component({
	standalone: true,
	selector: 'app-report-inline-text',
	templateUrl: './report-inline-text.component.html',
	styleUrls: ['./report-inline-text.component.scss'],
	imports: [
		NgFor,
		NgIf,
		MatTooltipModule,
		ReportFigureFlagsComponent,
		ReportRichTextComponent
	]
})
export class ReportInlineTextComponent implements OnInit, OnChanges, OnDestroy {
	/** What the field shows: the slide's own text, any edit already in it. */
	@Input() text = '';
	/** Where typing lands. Without one the text is only text. */
	@Input() field: ReportInlineTextField | null = null;
	/**
	 * The cover's two-tone hero, split the way the cover splits it. Drawn so
	 * while the reader is not typing; while they are, the field is one plain
	 * run of text, because the split is worked out again from what they type.
	 */
	@Input() head = '';
	@Input() accent = '';
	@Input() editorMode = false;
	/** What an empty field says, in the editor — a text block's heading. */
	@Input() placeholder = '';
	/** "Edited" beside typed text; off where the whole block is the reader's. */
	@Input() showEdited = true;
	/**
	 * The flags go under the text. Off where the slide draws them itself,
	 * outside a box that clips — the cover's clamped description.
	 */
	@Input() flagsBelow = true;

	@ViewChild('input') inputRef?: ElementRef<HTMLElement>;

	readonly editableMode = EDITABLE_MODE;

	editing = false;
	atLimit = false;
	/**
	 * The bound for this edit: the field's own, or the text's length when it
	 * is already longer (see REPORT_INLINE_TEXT_LIMITS).
	 */
	limit = 0;
	/** The two-tone hero has been swapped for the plain field. */
	opened = false;

	/** Where the text lives, for its flags (`figurePlace`). */
	place: string | null = null;
	/** The numbers to mark: the ones its flags are about. */
	marks: ReadonlySet<string> = NO_MARKS;
	/** The plain text in runs, the marked numbers apart. */
	runs: Array<{ text: string; marked: boolean }> = [];
	/** Some number in the plain text is marked. */
	marked = false;
	flagsShown = false;

	private readonly edits = inject(ReportInlineEditService, {
		optional: true
	});
	private readonly cdr = inject(ChangeDetectorRef);
	private original = '';
	private readonly place$ = new BehaviorSubject<string | null>(null);
	private readonly destroy$ = new Subject<void>();

	constructor(
		@Optional() private figureFlags: ReportFigureFlagsService | null
	) {}

	ngOnInit(): void {
		this.place$
			.pipe(
				distinctUntilChanged(),
				switchMap(place =>
					this.figureFlags && place
						? this.figureFlags.flagsFor(place)
						: of<ReportFigureFlag[]>([])
				),
				takeUntil(this.destroy$)
			)
			.subscribe(flags => {
				this.marks = new Set(flags.map(flag => flag.quoted));
				this.updateRuns();
			});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['field'] || changes['editorMode']) {
			this.place = this.editable ? figurePlace(this.field?.target) : null;
			this.place$.next(this.place);
		}
		this.flagsShown = this.flagsBelow && !!this.place;
		if (changes['text']) {
			this.updateRuns();
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	get editable(): boolean {
		return this.editorMode && !!this.field && !!this.edits;
	}

	/** The reader typed what the field holds now. Marked in the editor only. */
	get edited(): boolean {
		return this.editable && !!this.edits?.isEdited(this.field!.target);
	}

	get decorated(): boolean {
		return !!this.accent && !this.opened;
	}

	/** Open the two-tone hero for typing, with the caret where it was clicked. */
	open(event?: MouseEvent): void {
		if (!this.editable || this.opened || (event && event.button !== 0)) {
			return;
		}
		event?.preventDefault();
		this.opened = true;
		this.cdr.detectChanges();

		const input = this.inputRef?.nativeElement;
		if (!input) {
			return;
		}
		input.focus();
		if (event) {
			placeCaretAtPoint(input, event.clientX, event.clientY);
		} else {
			placeCaretAtEnd(input);
		}
	}

	/** Open the field for typing with the caret at the end — a heading block
	 *  just added. */
	focusField(): void {
		const input = this.inputRef?.nativeElement;
		if (!this.editable || !input) {
			return;
		}
		input.focus();
		placeCaretAtEnd(input);
	}

	onFocus(): void {
		if (!this.editable || this.editing) {
			return;
		}
		this.editing = true;
		this.original = this.text;
		this.limit = Math.max(this.field!.maxLength, this.original.length);
		this.atLimit = this.length() >= this.limit;
	}

	onBlur(): void {
		this.finish(true);
	}

	onKeydown(event: KeyboardEvent): void {
		if (!this.editing) {
			return;
		}
		if (event.key === 'Escape') {
			event.preventDefault();
			event.stopPropagation();
			this.finish(false);
			this.inputRef?.nativeElement.blur();
			return;
		}
		if (event.key !== 'Enter' || event.isComposing) {
			return;
		}
		if (event.shiftKey && this.field?.multiline) {
			return;
		}
		event.preventDefault();
		this.inputRef?.nativeElement.blur();
	}

	/**
	 * Refuse what would take the text past its bound, before it lands — the
	 * caret stays where it is, which a trim after the fact cannot promise.
	 */
	onBeforeInput(event: Event): void {
		const input = event as InputEvent;
		if (!this.editing || !this.field) {
			return;
		}
		const type = input.inputType ?? '';
		const breaksLine =
			type === 'insertParagraph' || type === 'insertLineBreak';
		if (breaksLine && !this.field.multiline) {
			input.preventDefault();
			return;
		}
		if (!type.startsWith('insert') || type === 'insertFromPaste') {
			return;
		}
		const added = breaksLine ? 1 : (input.data ?? '').length;
		if (
			added &&
			this.length() - this.selectedLength() + added > this.limit
		) {
			input.preventDefault();
			this.atLimit = true;
		}
	}

	/** What got past the check above — a composition, a drop — is trimmed. */
	onInput(): void {
		const input = this.inputRef?.nativeElement;
		if (!this.editing || !input) {
			return;
		}
		if (this.length() > this.limit) {
			input.textContent = this.visibleText().slice(0, this.limit);
			placeCaretAtEnd(input);
		}
		this.atLimit = this.length() >= this.limit;
	}

	onPaste(event: ClipboardEvent): void {
		if (!this.editing || !this.field) {
			return;
		}
		event.preventDefault();
		const pasted = (
			event.clipboardData?.getData('text/plain') ?? ''
		).replace(/\r\n?/g, '\n');
		const text = this.field.multiline
			? pasted
			: pasted.replace(/\s*\n\s*/g, ' ');
		const room = this.limit - (this.length() - this.selectedLength());
		const fitting = text.slice(0, Math.max(room, 0));
		if (!fitting) {
			this.atLimit = text.length > 0;
			return;
		}
		insertPlainText(fitting);
		this.atLimit = this.length() >= this.limit;
	}

	private finish(commit: boolean): void {
		const input = this.inputRef?.nativeElement;
		if (!this.editing || !input || !this.field) {
			return;
		}
		this.editing = false;
		this.atLimit = false;
		this.opened = false;

		const typed = commit
			? normalizeInlineText(input.innerText, this.field.multiline).slice(
					0,
					this.limit
			  )
			: '';
		const shown = normalizeInlineText(this.original, this.field.multiline);
		if (
			(typed || (commit && this.field.allowEmpty)) &&
			typed !== shown &&
			this.edits!.commit(this.field, typed)
		) {
			input.textContent = typed;
			return;
		}
		// Nothing to keep — Esc, an emptied field, the same words again: the
		// field shows exactly what it showed before it was clicked. A text
		// block's heading is the one field that may be emptied on purpose.
		input.textContent = this.original;
	}

	private visibleText(): string {
		return (this.inputRef?.nativeElement.innerText ?? '').replace(
			/\n$/,
			''
		);
	}

	/** A summary marks its own numbers (the rich field); a plain text is drawn here. */
	private updateRuns(): void {
		this.runs =
			this.marks.size && !this.field?.rich
				? markedRuns(this.text ?? '', this.marks)
				: [];
		this.marked = this.runs.some(run => run.marked);
	}

	private length(): number {
		return this.visibleText().length;
	}

	private selectedLength(): number {
		const input = this.inputRef?.nativeElement;
		const selection = window.getSelection();
		if (
			!input ||
			!selection?.rangeCount ||
			!input.contains(selection.anchorNode)
		) {
			return 0;
		}
		return selection.toString().length;
	}
}

interface CaretLookup {
	caretPositionFromPoint?(
		x: number,
		y: number
	): { offsetNode: Node; offset: number } | null;
	caretRangeFromPoint?(x: number, y: number): Range | null;
}

/**
 * Put the caret under the pointer. The plain field is drawn exactly where the
 * two-tone hero was, in the same face at the same size, so the point that was
 * clicked on one lands on the same letter of the other.
 */
function placeCaretAtPoint(element: HTMLElement, x: number, y: number): void {
	const lookup = document as unknown as CaretLookup;
	let range: Range | null = null;

	const position = lookup.caretPositionFromPoint?.(x, y);
	if (position && element.contains(position.offsetNode)) {
		range = document.createRange();
		range.setStart(position.offsetNode, position.offset);
	} else {
		const hit = lookup.caretRangeFromPoint?.(x, y) ?? null;
		if (hit && element.contains(hit.startContainer)) {
			range = hit;
		}
	}

	if (!range) {
		placeCaretAtEnd(element);
		return;
	}
	range.collapse(true);
	selectRange(range);
}

function placeCaretAtEnd(element: HTMLElement): void {
	const range = document.createRange();
	range.selectNodeContents(element);
	range.collapse(false);
	selectRange(range);
}

function selectRange(range: Range): void {
	const selection = window.getSelection();
	selection?.removeAllRanges();
	selection?.addRange(range);
}

/**
 * Insert at the caret as typing would. `insertText` keeps the insert on the
 * field's own undo stack, so Cmd+Z inside the field takes a paste back like
 * any other keystroke; the range fallback is for an engine without it.
 */
function insertPlainText(text: string): void {
	if (document.execCommand('insertText', false, text)) {
		return;
	}
	const selection = window.getSelection();
	if (!selection?.rangeCount) {
		return;
	}
	const range = selection.getRangeAt(0);
	range.deleteContents();
	range.insertNode(document.createTextNode(text));
	range.collapse(false);
	selectRange(range);
}
